import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { MessageTemplateStateModel } from 'models/setting/message-template/message-template.model';
import { v4 as uuidv4 } from 'uuid';
import {
  activeMessageTemplateAction,
  createMessageTemplateAction,
  deleteMessageTemplateAction,
  getMessageTemplateAction,
  inactiveMessageTemplateAction,
  listMessageTemplateAction,
  sendMessageTemplateToMemberAction,
  updateMessageTemplateAction,
} from './message-template.action';

const initialState: MessageTemplateStateModel = {
  messageTemplateListData: null,
  messageTemplateDetail: null,

  triggerSaveMessageTemplate: null,
  triggerSendMessageTemplateToMember: null,

  loadingList: false,
  loadingDetail: false,
  loadingSendMessageTemplateToMember: false,
};

export const messageTemplateSlice = createSlice({
  name: 'message-template',
  initialState,
  reducers: {
    setMessageTemplateStateAction: (
      state,
      action: PayloadAction<Partial<MessageTemplateStateModel>>,
    ) =>
      ({
        ...state,
        ...action?.payload,
      } as MessageTemplateStateModel),
    resetMessaStateAction: () => ({ ...initialState }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMessageTemplateAction.pending, (state) => ({
        ...state,
        messageTemplateDetail: null,
        loadingDetail: true,
      }))
      .addCase(getMessageTemplateAction.fulfilled, (state, { payload }) => ({
        ...state,
        messageTemplateDetail: payload?.data?.data || null,
        loadingDetail: false,
      }))
      .addCase(getMessageTemplateAction.rejected, (state) => ({
        ...state,
        messageTemplateDetail: null,
        loadingDetail: false,
      }))
      .addCase(listMessageTemplateAction.pending, (state) => ({
        ...state,
        loadingList: true,
      }))
      .addCase(listMessageTemplateAction.fulfilled, (state, { payload }) => ({
        ...state,
        loadingList: false,
        messageTemplateListData: {
          items: payload?.data?.items || [],
          totalItems: payload?.data?.totalItems || 0,
          totalPages: payload?.data?.totalPages || 0,
        },
      }))
      .addCase(listMessageTemplateAction.rejected, (state) => ({
        ...state,
        loadingList: false,
      }))
      .addCase(createMessageTemplateAction.fulfilled, (state) => ({
        ...state,
        triggerSaveMessageTemplate: { id: uuidv4() },
      }))
      .addCase(updateMessageTemplateAction.fulfilled, (state) => ({
        ...state,
        triggerSaveMessageTemplate: { id: uuidv4() },
      }))

      .addCase(deleteMessageTemplateAction.pending, (state) => ({
        ...state,
        loadingList: true,
      }))
      .addCase(deleteMessageTemplateAction.fulfilled, (state) => ({
        ...state,
        triggerSaveMessageTemplate: { id: uuidv4() },
      }))
      .addCase(deleteMessageTemplateAction.rejected, (state) => ({
        ...state,
        loadingList: false,
      }))
      .addCase(activeMessageTemplateAction.fulfilled, (state) => ({
        ...state,
        triggerSaveMessageTemplate: { id: uuidv4() },
        loadingList: false,
      }))
      .addCase(inactiveMessageTemplateAction.fulfilled, (state) => ({
        ...state,
        triggerSaveMessageTemplate: { id: uuidv4() },
        loadingList: false,
      }))
      .addCase(sendMessageTemplateToMemberAction.pending, (state) => ({
        ...state,
        loadingSendMessageTemplateToMember: true,
      }))
      .addCase(sendMessageTemplateToMemberAction.fulfilled, (state) => ({
        ...state,
        triggerSendMessageTemplateToMember: { id: uuidv4() },
        loadingSendMessageTemplateToMember: false,
      }))
      .addCase(sendMessageTemplateToMemberAction.rejected, (state) => ({
        ...state,
        loadingSendMessageTemplateToMember: false,
      }));
  },
});

export const { setMessageTemplateStateAction, resetMessaStateAction } =
  messageTemplateSlice.actions;

export default messageTemplateSlice.reducer;
